import { unsafeCSS, html } from 'lit';
import { property, queryAll, state } from 'lit/decorators.js';

import { Constants as ProductCenterFilterConstants } from '../product-center-filters.constants';
import { PopBaseComponent } from '../../../base/pop-base-component.js';
import { Constants } from './filter-chips.constants.js';
import { IconSVG } from '../../../common/utils/icon.js';
import { Chip, ChipCategory } from './chip/chip.model.js';
import productCenterStore, {
  zustandStateToComponent,
} from '../../store/product-center.store';
import {
  FilterCategoryField,
  type IFilters,
  type IProductCenterStore,
} from '../../store/product-center.store.model';
import type { FilterChips } from './filter-chips.model.js';
import { Constants as ProductCenterTableWrapperConstants } from '../../product-center-table-wrapper/product-center-table-wrapper.constants';
import { updateUrl } from '../../product-center-table-wrapper/product-center-table-wrapper.utils';
import { View } from '../../product-center-table-wrapper/product-center-table-wrapper.model';
import style from './filter-chips.scss?inline';

/**
 * @tag pop-pc-filter-chips-component
 * @attribute {FilterChips} [data-component-object] Filter Chips content
 */
export class PopFilterChipsComponent extends PopBaseComponent {
  static get styles() {
    return [...super.styles, unsafeCSS(style)];
  }

  static readonly tagName = Constants.TAG_NAME;

  /**
   * @internal
   */
  @state()
  productCenterState: IProductCenterStore = productCenterStore.getState();

  /**
   * @internal
   */
  @property()
  private readonly filters!: IFilters;

  /**
   * @internal
   */
  @property()
  selectedView?: View;

  @state()
  private readonly filterChips: FilterChips = {
    chips: [],
  };

  @state()
  private showAllChips = false;

  @queryAll('.chip')
  private readonly chipList!: NodeListOf<HTMLElement>;

  constructor() {
    super();
    this.enableI18n = true;
  }

  /*
   * Component Methods
   */

  protected firstUpdated() {
    //Add event listener to update URL when chips are removed
    this.addEventListener(
      ProductCenterTableWrapperConstants.UPDATE_URL_EVENT,
      () => updateUrl(this.filters, this.selectedView ?? View.TABLE),
    );
  }

  private async toggleShowAllChips() {
    // Toggle the state to show or hide all chips
    this.showAllChips = !this.showAllChips;
    // Set focus to the 6th chip when showing all chips
    await this.updateComplete;
    if (this.showAllChips && this.chipList?.length > Constants.CHIPS_LIMIT) {
      // Set focus to the 6th chip
      const chipCloseBtn = this.chipList?.[
        Constants.CHIPS_LIMIT
      ].shadowRoot?.querySelector('.close-chip') as HTMLElement;
      chipCloseBtn?.focus();
    }
    if (!this.showAllChips) {
      // Set focus to the first chip
      const showButton = this.shadowRoot?.querySelector(
        '.show-button-label',
      ) as HTMLElement;
      showButton?.blur();
      showButton?.focus();
    }
  }

  private removeFilterListener(e: CustomEvent) {
    this.productCenterState.setFilteredProducts({
      categoryField: e.detail.categoryField,
      filterValue: e.detail.filterValue,
    });

    // Dispatch event to trigger URL update
    this.dispatchEvent(
      new CustomEvent(ProductCenterTableWrapperConstants.UPDATE_URL_EVENT, {
        bubbles: true,
        composed: true,
      }),
    );
    this.requestUpdate();
  }

  private resetFilterHandler() {
    this.productCenterState.resetFilters();
    this.requestUpdate();
    const componentsInDOM = this.parentElement
      ?.querySelector(ProductCenterFilterConstants.TAG_NAME)
      ?.shadowRoot?.querySelectorAll(
        ProductCenterFilterConstants.BEACON_MULTI_SELECT,
      ) as NodeListOf<HTMLFormElement> | null;
    componentsInDOM?.forEach((component) => component.reset());

    // Dispatch event to trigger URL update
    this.dispatchEvent(
      new CustomEvent(ProductCenterTableWrapperConstants.UPDATE_URL_EVENT, {
        bubbles: true,
        composed: true,
      }),
    );
  }

  private buildFilterChipsObject() {
    this.filterChips.chips = [];
    let chipIdCounter = 1;

    Object.keys(this.filters).forEach((key) => {
      const categoryField = key as FilterCategoryField;
      const filterValues = this.filters[categoryField];

      if (filterValues) {
        filterValues.forEach((value) => {
          if (value.isSelected) {
            const chipId = chipIdCounter;
            chipIdCounter++;
            const chip: Chip = {
              categoryField: categoryField,
              chipId: chipId,
              label: value.name,
            };
            this.filterChips.chips.push(chip);
          }
        });
      }
    });
  }

  private getCategoryId(categoryField: FilterCategoryField): number {
    const categoryMap: { [key in FilterCategoryField]: number } = {
      [FilterCategoryField.PRODUCT_RANGE]: ChipCategory.PRODUCT_RANGE,
      [FilterCategoryField.ASSET_CLASS]: ChipCategory.ASSET_CLASS,
      [FilterCategoryField.GEOGRAPHICAL_EXPOSURE]:
        ChipCategory.GEOGRAPHICAL_EXPOSURE,
      [FilterCategoryField.DISTRIBUTION_FREQUENCY]:
        ChipCategory.DISTRIBUTION_FREQUENCY,
    };

    return categoryMap[categoryField] || 0;
  }

  renderComponent() {
    if (
      !Object.values(this.filters).some(
        (filterArray) =>
          filterArray && filterArray.some((filter) => filter.isSelected),
      )
    ) {
      return html``;
    }

    this.buildFilterChipsObject();

    const chips = this.filterChips.chips;

    const sortedChips = chips.slice().sort((a: Chip, b: Chip) => {
      if (a.categoryField === b.categoryField) {
        return a.label.localeCompare(b.label);
      }
      return (
        this.getCategoryId(a.categoryField) -
        this.getCategoryId(b.categoryField)
      );
    });

    let lastCategoryId: ChipCategory | null = null;

    const showButtonLabel = this.showAllChips
      ? this.localize(Constants.TRANSLATION_KEYS.SHOW_LESS)
      : `${this.localize(Constants.TRANSLATION_KEYS.SHOW_PLUS)}${chips.length - Constants.CHIPS_LIMIT}`;

    const showButtonAriaLive =
      showButtonLabel === this.localize(Constants.TRANSLATION_KEYS.SHOW_LESS)
        ? 'polite'
        : 'off';

    return html`
      <div class="chips-container">
        <div class="container-label">
          ${this.localize(Constants.TRANSLATION_KEYS.YOUR_SELECTIONS_LABEL)}:
        </div>
        <div class="chips-list">
          ${sortedChips.map((chip: Chip) => {
            const separator =
              lastCategoryId !== null &&
              lastCategoryId !== this.getCategoryId(chip.categoryField)
                ? html`<div class="category-separator"></div>`
                : null;
            lastCategoryId = this.getCategoryId(chip.categoryField);
            return html`
              ${separator}
              <pop-pc-chip-component
                class="chip ${chip.chipId > Constants.CHIPS_LIMIT &&
                !this.showAllChips
                  ? 'hide-chip'
                  : ''}"
                @close-chip="${this.removeFilterListener}"
                data-component-object="${JSON.stringify(chip)}"
              ></pop-pc-chip-component>
            `;
          })}
          ${chips.length > Constants.CHIPS_LIMIT
            ? html`
                <div class="show-button">
                  <beacon-button
                    @beacon-event-click=${this.toggleShowAllChips}
                    class="show-button-label"
                    variant="tertiary"
                    scale="normal"
                    aria-label="${showButtonLabel}"
                    aria-live="${showButtonAriaLive}"
                  >
                    ${showButtonLabel}
                  </beacon-button>
                </div>
              `
            : null}
          ${chips.length > 0
            ? html`
                <beacon-button
                  @beacon-event-click=${this.resetFilterHandler}
                  class="reset-filter"
                  icon-align="left"
                  variant="tertiary"
                  scale="normal"
                >
                  ${IconSVG.circularClose(undefined, {
                    slot: ProductCenterTableWrapperConstants.PREFIX_SLOT,
                    customClass:
                      ProductCenterTableWrapperConstants.CHEVRON_CUSTOM_CLASS,
                  })}
                  ${this.localize(Constants.TRANSLATION_KEYS.RESET_FILTERS)}
                </beacon-button>
              `
            : null}
        </div>
      </div>
    `;
  }

  render() {
    return this.renderComponent();
  }
}

customElements.define(
  Constants.TAG_NAME,
  zustandStateToComponent(
    {
      mapState: (productCenterState) => {
        return {
          filters: productCenterState.filters,
          selectedView: productCenterState.selectedView,
        };
      },
    },
    PopFilterChipsComponent,
  ),
);
